import { useCallback, useMemo, useRef } from "react";
import { AsyncMultiselectFieldProps } from "./AsyncMultiselectField";
import { Predicate, Query } from "@syncfusion/ej2-data";
import React from "react";
import {
  MultiSelectComponent,
  MultiSelectChangeEventArgs,
  FocusEventArgs,
  PopupEventArgs,
} from "@syncfusion/ej2-react-dropdowns";
import { useOData } from "../../../../stores/OData/useOData";
import { resolveODataUrl } from "../types";

export const useAsyncMultiselectField = (props: AsyncMultiselectFieldProps) => {
  const { setupAsyncMultiselectODataSource, odataReady } = useOData({
    getAuthRequestHeader:
      props.getAuthRequestHeader || (() => Promise.resolve({})),
  });

  const dataSource = useMemo(() => {
    if (props.dataSource) {
      return props.dataSource;
    }

    if (!odataReady) return null;

    const resolvedODataUrl = resolveODataUrl({
      odataUrl: props.odataUrl,
      baseODataUrl: props.baseODataUrl,
      oDataKey: props.oDataKey,
    });

    return setupAsyncMultiselectODataSource(
      resolvedODataUrl,
      props.groupByActiveStatus
    );
  }, [
    props.dataSource,
    odataReady,
    props.odataUrl,
    props.baseODataUrl,
    props.oDataKey,
    props.groupByActiveStatus,
    setupAsyncMultiselectODataSource,
  ]);

  const multiSelectRef = React.useRef<MultiSelectComponent | null>(null);

  // Because the handlers in syncfusion are not reactive
  const localOpen = useRef(false);

  const getFields = ({
    textColumnName,
    valueColumnName,
    groupByActiveStatus,
  }: {
    textColumnName?: string;
    valueColumnName?: string;
    groupByActiveStatus?: boolean;
  }) => {
    return {
      text: textColumnName ?? "label",
      value: valueColumnName ?? "value",
      groupBy: !!groupByActiveStatus ? "category" : undefined,
    };
  };

  const fields = useMemo(() => {
    return getFields({
      textColumnName: props.textColumnName,
      valueColumnName: props.valueColumnName,
      groupByActiveStatus: props.groupByActiveStatus,
    });
  }, [props.textColumnName, props.valueColumnName, props.groupByActiveStatus]);

  const query = useMemo(() => {
    const fields = getFields({
      textColumnName: props.textColumnName,
      valueColumnName: props.valueColumnName,
      groupByActiveStatus: props.groupByActiveStatus,
    });

    const predicates = [...(props.conditions || [])];

    let where: Predicate | undefined;

    if (predicates) {
      predicates.forEach((predicate) => {
        where = where ? where.and(predicate) : predicate;
      });
    }

    let query = new Query().select([
      fields.text,
      fields.value,
      ...(props.optionFields || []),
    ]);

    if (where) {
      query = query.where(where);
    }
    query = query.sortBy(fields.text).requiresCount();

    return query;
  }, [
    props.conditions,
    props.optionFields,
    props.textColumnName,
    props.valueColumnName,
    props.groupByActiveStatus,
  ]);

  const focus = (_e: FocusEventArgs) => {
    // This is the cleanest way I could find to support triggering via focus without breaking the open on click functionality.
    setTimeout(() => {
      if (!localOpen.current) {
        multiSelectRef.current?.showPopup();
      }
    }, 100);
  };

  const open = (_e: PopupEventArgs) => {
    localOpen.current = true;
  };

  const close = (_e: PopupEventArgs) => {
    localOpen.current = false;
  };

  const setNoRecordsTemplate = useCallback(() => {
    if (!!multiSelectRef.current && !!props.noRecordsTemplate) {
      multiSelectRef.current.noRecordsTemplate = props.noRecordsTemplate(
        multiSelectRef.current
      ) as any;
    }
  }, [props.noRecordsTemplate]);

  const created = useCallback(() => {
    setNoRecordsTemplate();
  }, [setNoRecordsTemplate]);

  const change = useCallback(
    async (e: MultiSelectChangeEventArgs) => {
      // Syncfusion emits a change event on load of the initial data.
      // We only want to update the form state if the change was user-intiated.
      if (props.onSubmit && e.isInteracted) {
        const values = e.value.map((v) =>
          v instanceof Object ? (v as any)[props.valueColumnName ?? "value"] : v
        );
        props.onSubmit(values);
      }
    },
    [props.onSubmit, props.valueColumnName]
  );

  return {
    dataSource,
    focus,
    multiSelectRef,
    change,
    fields,
    query,
    created,
    open,
    close,
  };
};
